import React, { useState, useEffect } from 'react';
import { Filter, DollarSign, Star, Eye } from 'lucide-react';

const defaultTheme = {
  card: 'white',
  border: '#e5e7eb',
  text: '#111827',
  textMuted: '#374151',
  textMuted2: '#6b7280',
  inputBorder: '#d1d5db',
  primary: '#0d9488',
  primaryLight: '#ccfbf1',
  accentLight: '#d1fae5',
  accentText: '#065f46',
  errorBg: '#fee2e2',
  errorText: '#991b1b',
  shadow: '0 1px 3px rgba(0,0,0,0.1)',
};

function FilterPanel({ competitors, filters, onFilterChange, selectedCount = 0, theme }) {
  const t = theme || defaultTheme;
  const [localFilters, setLocalFilters] = useState({
    minPrice: filters.minPrice || '',
    maxPrice: filters.maxPrice || '',
    minSentiment: filters.minSentiment || 0,
    showOnlySelected: filters.showOnlySelected || false,
    ...filters,
  });

  // Calculate price range from competitors
  const priceStats = React.useMemo(() => {
    if (!competitors || competitors.length === 0) {
      return { min: 0, max: 1000 };
    }
    
    const prices = competitors
      .map(c => c.current_price)
      .filter(p => p !== null && p !== undefined);
    
    if (prices.length === 0) return { min: 0, max: 1000 };
    
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }, [competitors]);

  useEffect(() => {
    onFilterChange(localFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters]);

  const handleChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div style={{
      backgroundColor: t.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: t.shadow,
      marginBottom: '20px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${t.border}`,
      }}>
        <Filter size={20} color={t.primary} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: t.text }}>
          Filters
        </h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        {/* Price Range */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500', color: t.textMuted, marginBottom: '8px' }}>
            <DollarSign size={14} />
            Price Range
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="number"
              placeholder={`Min ($${Math.round(priceStats.min)})`}
              value={localFilters.minPrice}
              onChange={(e) => handleChange('minPrice', e.target.value)}
              style={{
                flex: 1,
                padding: '8px 10px',
                fontSize: '13px',
                border: `1px solid ${t.inputBorder}`,
                borderRadius: '6px',
                backgroundColor: t.card,
                color: t.text,
              }}
            />
            <span style={{ color: t.textMuted2 }}>to</span>
            <input
              type="number"
              placeholder={`Max ($${Math.round(priceStats.max)})`}
              value={localFilters.maxPrice}
              onChange={(e) => handleChange('maxPrice', e.target.value)}
              style={{
                flex: 1,
                padding: '8px 10px',
                fontSize: '13px',
                border: `1px solid ${t.inputBorder}`,
                borderRadius: '6px',
                backgroundColor: t.card,
                color: t.text,
              }}
            />
          </div>
        </div>

        {/* Sentiment Filter */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500', color: t.textMuted, marginBottom: '8px' }}>
            <Star size={14} />
            Min Sentiment Score: {(localFilters.minSentiment * 100).toFixed(0)}%
          </label>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            padding: '8px 0',
          }}>
            <span style={{ fontSize: '11px', color: t.textMuted2, minWidth: '30px' }}>0%</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={localFilters.minSentiment}
              onChange={(e) => handleChange('minSentiment', parseFloat(e.target.value))}
              style={{ 
                flex: 1,
                height: '20px',
              }}
            />
            <span style={{ fontSize: '11px', color: t.textMuted2, minWidth: '35px' }}>100%</span>
          </div>
        </div>

        {/* Show Only Selected Toggle */}
        {selectedCount > 0 && (
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500', color: t.textMuted, marginBottom: '8px' }}>
              <Eye size={14} />
              View Options
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 0' }}>
              <button
                onClick={() => handleChange('showOnlySelected', !localFilters.showOnlySelected)}
                style={{
                  position: 'relative',
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  backgroundColor: localFilters.showOnlySelected ? t.primary : t.inputBorder,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  padding: 0,
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: localFilters.showOnlySelected ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: t.card,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
              <span style={{ fontSize: '13px', color: t.textMuted }}>
                Show only selected ({selectedCount})
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Active Filters Summary */}
      {(localFilters.minPrice || localFilters.maxPrice || localFilters.minSentiment > 0 || localFilters.showOnlySelected) && (
        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${t.border}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: t.textMuted2 }}>Active filters:</span>
            {localFilters.minPrice && (
              <span style={{ fontSize: '12px', padding: '4px 10px', backgroundColor: t.primaryLight, color: t.primary, borderRadius: '12px' }}>
                Min: ${localFilters.minPrice}
              </span>
            )}
            {localFilters.maxPrice && (
              <span style={{ fontSize: '12px', padding: '4px 10px', backgroundColor: t.primaryLight, color: t.primary, borderRadius: '12px' }}>
                Max: ${localFilters.maxPrice}
              </span>
            )}
            {localFilters.minSentiment > 0 && (
              <span style={{ fontSize: '12px', padding: '4px 10px', backgroundColor: t.primaryLight, color: t.primary, borderRadius: '12px' }}>
                Sentiment ≥ {(localFilters.minSentiment * 100).toFixed(0)}%
              </span>
            )}
            {localFilters.showOnlySelected && (
              <span style={{ fontSize: '12px', padding: '4px 10px', backgroundColor: t.accentLight, color: t.accentText, borderRadius: '12px' }}>
                Showing only selected
              </span>
            )}
            <button
              onClick={() => setLocalFilters({ minPrice: '', maxPrice: '', minSentiment: 0, showOnlySelected: false })}
              style={{
                fontSize: '12px',
                padding: '4px 10px',
                backgroundColor: t.errorBg,
                color: t.errorText,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
              }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
